import DropdownSelection from "@app-components/dropdown-selection/dropdown-selection.vue";
import ReservationFormBase from "@app-components/reservation-form-base/reservation-form-base.vue";
import usei18n from "@app-i18n/index";
import useAttractionsRestClient from "@app-rest/attractions";
import { IAttraction } from "@app-rest/attractions.interfaces";
import { IPlayersQuantity, IPlayerType } from "@app-rest/systems.interfaces";
import { home } from "@app-router/routes/reservations";
import useLangStore from "@app-store/lang";
import useSystemStore from "@app-store/system";
import { dateKey, format, stringToDate, stringToDateUTC } from "@app-utilities/dates";
import { useRuleBowlersRequired, useRuleDateRequired } from "@app-utilities/rules";
import { QFormFieldDate, QFormFieldInput, QFormFieldPlayers, QFormFieldTime,QOverlay, useResponsiveUtilities } from "@qamf/lighthouse";
import { addHours, isAfter, isBefore, Locale, set, setMinutes, startOfDay, subDays, subMinutes } from "date-fns";
import { computed, defineComponent, nextTick, onBeforeMount, onMounted, onUnmounted, PropType, ref, useTemplateRef, watch, watchEffect } from "vue";
import { useRouter } from "vue-router";

export interface IAttractionReservationFormData {
	attraction: IDropDownOption | null;
	date: Date | null;
	datetime: Date | null;
	players: IPlayersQuantity[];
}
interface IDropDownOption {
	text: string;
	isDisabled?: boolean;
	value: string | number;
	href?: string
}

export interface IPlayersTypes {
	MaxPlayers: number;
	MinPlayers?: number;
	Types: IPlayerType[];
}

export const playerTypesData = {
	MaxPlayers: 1,
	MinPlayers: 1,
	Types: [
		{
			Id: 1,
			Name: "Players",
			Active: true
		}
	]
};

export default defineComponent({
	name: "AttractionsReservationForm",
	components: {
		QOverlay,
		DropdownSelection,
		QFormFieldDate,
		QFormFieldInput,
		QFormFieldTime,
		QFormFieldPlayers,
		ReservationFormBase
	},
	props: {
		initialFormData: { type: Object as PropType<IAttractionReservationFormData>, default: null },
		compact: { type: Boolean, default: false }
	},
	emits: ["submit", "form-ready"],
	setup(props, { emit }) {
		const { translateKey } = usei18n();
		const router = useRouter();
		const langStore = useLangStore();
		const systemStore = useSystemStore();
		const reservationFormBase = useTemplateRef<InstanceType<typeof ReservationFormBase> | null>("reservationFormBase");
		const dropdown = useTemplateRef<InstanceType<typeof DropdownSelection> | null>("dropdown");
		const datePicker = useTemplateRef<InstanceType<typeof QFormFieldDate> | null>("datePicker");
		const timePicker = useTemplateRef<InstanceType<typeof QFormFieldTime> | null>("timePicker");
		const playersPicker = useTemplateRef<InstanceType<typeof QFormFieldPlayers> | null>("playersPicker");
		const selectedAttraction = ref<IDropDownOption | null>(null);
		const timePicked = ref<Date | null>(null);
		const datePicked = ref<Date | null>(new Date());
		const players = ref<IPlayersQuantity[]>([]);
		const dateFnsLocale = ref<Locale | undefined>();
		const disabledDates = ref<Date[] | undefined>();
		const playerTypes = ref<IPlayersTypes | null>(null);
		const filteredAttractions = ref<IAttraction[] | null>(null);
		const attractionOptions = ref<IDropDownOption[]>();
		const responsiveUtilities = useResponsiveUtilities();
		const isSmall = computed(() => responsiveUtilities.isSmall.value );
		const showCompactView = computed(() => props.compact && isSmall.value);

		const isDateValid = computed(() => {
			return Boolean(datePicked.value);
		});
		const isTimeValid = computed(() => {
			return Boolean(timePicked.value);
		});
		const isPlayersValid = computed(() => {
			return Boolean(players.value.length);
		});
		const is24h = computed(() => {
			return langStore.language?.is24h;
		});
		const timeSeparator = computed(() => {
			return langStore.language?.timeSep;
		});

		const getOpeningDate = () => {
			if(!datePicked.value) return undefined;
			const dateString = dateKey(datePicked.value);
			return systemStore.getOpeningDay(dateString);
		};
		const openingDatetimeOpen = computed(() => {
			const openingDate = getOpeningDate();
			if (!openingDate) return undefined;
			const openingDateTime = openingDate.StartBookingTime ? stringToDateUTC(openingDate.StartBookingTime) : undefined;
			if (!openingDateTime) return undefined;
			const now = new Date(openingDateTime);
			const nowRoundedToPreviousHour = setMinutes(now, 0);
			const hourToUse = isAfter(now, nowRoundedToPreviousHour) ? addHours(nowRoundedToPreviousHour, 1) : now;
			return hourToUse;
		});
		const openingDatetimeClose = computed(() => {
			const openingDate = getOpeningDate();
			if (!openingDate) return undefined;
			return openingDate.EndBookingTime ? stringToDateUTC(openingDate.EndBookingTime) : undefined;
		});
		const openingDatetimeCloseRoundedUp = computed(() => {
			if (!openingDatetimeClose.value) return undefined;
			return subMinutes(openingDatetimeClose.value, 15);
		});
		const loadingOpenings = computed(() => {
			return systemStore.loadingOpenings;
		});
		const minutesDayChangeTime = computed(() => {
			if (!systemStore.info || !systemStore.info.DayChangeTime) return;

			const timeSep = ":";
			return +(systemStore.info.DayChangeTime.split(timeSep)[1]);
		});

		const hoursDayChangeTime = computed(() => {
			if (!systemStore.info || !systemStore.info.DayChangeTime) return;

			const timeSep = ":";
			return +(systemStore.info.DayChangeTime.split(timeSep)[0]);
		});

		const isPreviousDay = (date: Date) => {
			const dayChangeTime = set(date, {
				hours: hoursDayChangeTime.value,
				minutes: minutesDayChangeTime.value
			});

			return isBefore(date, dayChangeTime);
		};

		const minDateCalendar = computed(() => {
			const now = new Date();
			const minEvaluated = isPreviousDay(now) ? subDays(now, 1) : now;
			return minEvaluated;
		});
		const maxDateCalendar = computed(() => {
			const lastBookableDay = systemStore.lastBookableDay;
			if (lastBookableDay) return startOfDay(new Date(lastBookableDay));

			return undefined;
		});
		const openingDates = computed(() => {
			return systemStore.openingTimes?.Dates ?? [];
		});
		const ensureOpeningsFrom = async(date: Date) => {
			await systemStore.ensureOpenings(date);
		};
		const retrieveDisabledDates = () => {
			if (openingDates.value.length === 0) return undefined;

			return openingDates.value.filter(date => date.IsOpen === false).map(date => stringToDate(date.Date)).filter((el) => el !== undefined);
		};
		const getOpeningDayFrom = (date: Date) => {
			const formatted = dateKey(date);
			const dateInfo = systemStore.getOpeningDay(formatted);
			if (dateInfo?.IsOpen) return startOfDay(date);
			else {
				const nextOpen = systemStore.getNextOpenDay(date);
				if (!nextOpen) return null;
				const openDate = stringToDateUTC(nextOpen.Date);
				if (!openDate) return null;
				return startOfDay(openDate);
			}
		};

		const attractionLabel = computed(() => {
			return selectedAttraction.value ? `${selectedAttraction.value.text}` : "";
		});
		const dateLabel = computed(() => {
			const attractionLabel = selectedAttraction.value ? `${selectedAttraction.value.text}` : "";
			const dateLabel = datePicked.value ? ` | ${format(datePicked.value, "EEE, MMM d")}` : "";
			return `${attractionLabel} ${dateLabel}`;
		});
		const timeLabel = computed(() => {
			const attractionLabel = selectedAttraction.value ? `${selectedAttraction.value.text}` : "";
			const validDate = timePicked.value ?? datePicked.value;
			const dateLabel = validDate ? ` | ${format(validDate, "EEE, MMM d")}` : "";
			const timeLabel = timePicked.value ? ` | ${format(timePicked.value, is24h.value ? "H" : "h a")}` : "";
			return `${attractionLabel} ${dateLabel} ${timeLabel}`;
		});
		const totalPlayers = computed(() => {
			return players.value.reduce((accumulator, type) => accumulator + type.Quantity, 0);
		});
		const compactInputLabel = computed(() => {
			const label = totalPlayers.value === 1
				? translateKey("label_player")
				: translateKey("label_players");
			const attractionLabel = selectedAttraction.value ? `${selectedAttraction.value.text}, ` : "";
			const validDate = timePicked.value ?? datePicked.value;
			const dateLabel = validDate ? `${format(validDate, "P")}, ` : "";
			const timeLabel = timePicked.value ? `${format(timePicked.value, is24h.value ? "H" : "h a")}, ` : "";
			return `${attractionLabel}${dateLabel}${timeLabel}${totalPlayers.value} ${label}`;
		});
		const extractHour = (date: Date | undefined) => {
			if (!date) return 0;
			return date.getHours();
		};
		const formatDate = (ymd: Date | null) => {
			return ymd ? format(ymd, "EEEE, MMM d") : "";
		};
		const formatDateTime = (ymd: Date | null) => {
			return ymd ? format(ymd, "EEEE, MMM d - p") : "";
		};
		const todayLabel = computed(() => {
			return translateKey("label_today");
		});
		const timePlaceholder = computed(() => {
			return translateKey("label_starting_time");
		});

		const hideOverlay = () => {
			reservationFormBase.value?.hideOverlay();
		};
		const showOverlay = () => {
			reservationFormBase.value?.showOverlay();
		};
		const closeAllPopOver = () => {
			dropdown.value?.hidePopover();
			datePicker.value?.hidePopover();
			timePicker.value?.hidePopover();
			playersPicker.value?.hidePopover();
			hideOverlay();
		};

		const goToDropdown = () => {
			closeAllPopOver();
			dropdown.value?.openPopover();
		};
		const goToDatePicker = () => {
			closeAllPopOver();
			datePicker.value?.openPopover();
		};
		const goToTimePicker = () => {
			closeAllPopOver();
			timePicker.value?.openPopover();
		};
		const goToPlayers = () => {
			closeAllPopOver();
			playersPicker.value?.openPopover();
		};

		const onAttractionSelected = () => {
			if(!isSmall.value)
				goToDatePicker();
		};
		const onDateSelected = () => {
			if(!isSmall.value)
				goToTimePicker();
		};
		const onTimeSelected = () => {
			if(!isSmall.value)
				goToPlayers();
		};
		const onDateChanged = () => {
			if (datePicked.value && timePicked.value)
				timePicked.value = null;
		};

		const trySubmit = async(): Promise<void> => {
			closeAllPopOver();
			if (!selectedAttraction.value)
				return dropdown.value?.openPopover();

			if (!datePicked.value)
				return datePicker.value?.openPopover();

			if (!timePicked.value)
				return timePicker.value?.openPopover();

			if (!players.value.length)
				return playersPicker.value?.openPopover();

			if (
				!selectedAttraction.value
				|| !datePicked.value
				|| !timePicked.value
                || !players.value.length
			)
				return;

			const currentFormData = {
				attraction: selectedAttraction.value,
				date: datePicked.value,
				datetime: timePicked.value,
				players: players.value
			};

			emit("submit", currentFormData);
		};

		watchEffect(() => {
			if (selectedAttraction.value?.text && filteredAttractions.value?.length) {
				const currentAttraction = filteredAttractions.value.find(attr => {
					return typeof selectedAttraction.value?.value === "number" && attr.Id === selectedAttraction.value.value;
				});

				const currentCapacity = currentAttraction
					? Math.max(...currentAttraction.Resources.map(resource => {
						if (resource.Status === "Active") return resource.MaxCapacity;
						return 0;
					}))
					: 0;
				playerTypes.value = {
					...playerTypesData,
					MaxPlayers: currentCapacity
				};

				if (currentCapacity < players.value[0].Quantity)
					players.value = [{ Id: 1, Name: "Players", Active: true, Quantity: 1 }];
			}
		});
		watch(datePicked, async() => {
			if (!timePicked.value) {
				await nextTick();
				timePicker.value?.selectFirstAvailableTime();
			}
		});

		onBeforeMount(() => {
			useRuleDateRequired("date-required");
			useRuleBowlersRequired("players-required");
		});
		onMounted(async() => {
			dateFnsLocale.value = langStore.libDictionary.get("date-fns");

			const rest = useAttractionsRestClient();
			if (systemStore.info) {
				const elements = await rest.getAttractionsList(systemStore.info?.Id);
				const filteredElements = elements.data ? elements.data.Elements.filter( el =>
					el.Status === "Active"
					&& el.PlayingMode === 'PreDefined'
				)
				: [];

				filteredAttractions.value = filteredElements;

				const formattedElements = filteredElements
					.map(el => ({
						text: el.Title,
						value: el.Id
					}));
				attractionOptions.value = formattedElements.length
					? [...formattedElements, {
						text: translateKey("want_to_reserve_bowling"),
						value: "want_to_reserve_bowling",
						href: router.resolve({ name: home }).href
					}]
					: [];
				selectedAttraction.value = props.initialFormData.attraction ?? formattedElements[0];

				players.value = props.initialFormData.players.length
					? [...props.initialFormData.players]
					: [{ Id: 1, Name: "Players", Active: true, Quantity: 1 }];
			}

			const currentDate = props.initialFormData?.date ?? new Date();
			const datetime = props.initialFormData?.datetime ?? new Date();
			const yesterday = subDays(currentDate, 1);
			await ensureOpeningsFrom(yesterday);
			datePicked.value = getOpeningDayFrom(currentDate);
			disabledDates.value = retrieveDisabledDates();
			if (datePicked.value) {
				if (props.initialFormData.datetime)
					timePicked.value = datetime;
			}
			responsiveUtilities.init();
			emit("form-ready");
		});
		onUnmounted(() => {
			responsiveUtilities.destroy();
		});

		return {
			reservationFormBase,
			dropdown,
			datePicker,
			timePicker,
			playersPicker,
			selectedAttraction,
			datePicked,
			timePicked,
			players,
			attractionOptions,
			isSmall,
			showCompactView,
			dateFnsLocale,
			disabledDates,
			isDateValid,
			isTimeValid,
			isPlayersValid,
			is24h,
			timeSeparator,
			openingDatetimeOpen,
			openingDatetimeClose,
			openingDatetimeCloseRoundedUp,
			loadingOpenings,
			minDateCalendar,
			maxDateCalendar,
			playerTypes,
			attractionLabel,
			dateLabel,
			timeLabel,
			totalPlayers,
			compactInputLabel,
			extractHour,
			formatDate,
			formatDateTime,
			todayLabel,
			timePlaceholder,
			showOverlay,
			hideOverlay,
			closeAllPopOver,
			goToDropdown,
			goToDatePicker,
			goToTimePicker,
			goToPlayers,
			onAttractionSelected,
			onDateSelected,
			onTimeSelected,
			onDateChanged,
			trySubmit
		};
	}
});
